import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { DOCUMENT } from '@angular/common';

/**
 * Builds URLs for products and categories used by both <link rel="canonical">
 * tags (SEO truth) and routerLinks (UX-friendly internal navigation). These
 * two needs diverge:
 *
 *   - Canonical tag must be deterministic per product so Google deduplicates
 *     all variant URLs to one. We use /itm/{slug}?itm={id} — independent of
 *     which category the user came from, the product belongs to, etc.
 *
 *   - Internal navigation (product-box, breadcrumbs) benefits from keeping
 *     the category prefix so URLs stay semantic during browsing. We keep
 *     /{category}/itm/{slug}?itm={id} for those, falling back to the user's
 *     current category when product data lacks category info.
 *
 * Backend resolves products by ?itm={id} only — the slug and category prefix
 * are SEO/UX decoration — so every variant URL above lands on the same
 * product. The canonical tag tells Google which one to actually index.
 *
 * LOCALE AWARENESS: the English build is served under /en (angular.json i18n
 * subPath), but router.url and document base strip that prefix — so a canonical
 * built as origin + path silently pointed /en pages at the Serbian root URLs,
 * telling Google the whole English site is a duplicate of the Serbian one.
 * Every absolute URL built here therefore carries the locale prefix of the
 * RUNNING build (LOCALE_ID): '' for the sr-Latn source locale, '/en' for en.
 */
@Injectable({ providedIn: 'root' })
export class ProductUrlService {

  /**
   * URL prefix of the currently running locale build: '' for the source locale
   * (sr-Latn, served at the site root), '/{subtag}' for translated builds
   * ('/en'). Mirrors the subPath configuration in angular.json i18n.
   */
  public readonly localePrefix: string;

  constructor(@Inject(DOCUMENT) private document: Document,
              @Inject(LOCALE_ID) localeId: string) {
    if (localeId && !localeId.toLowerCase().startsWith('sr')) {
      this.localePrefix = '/' + localeId.split('-')[0].toLowerCase();
    } else {
      this.localePrefix = '';
    }
  }

  /**
   * Absolute canonical URL for a product, suitable for <link rel="canonical">.
   * Always returns {localePrefix}/itm/{slug}?itm={id} — deterministic, one URL
   * per product regardless of where the user navigated from or what category
   * data the backend returned.
   */
  public getCanonicalUrl(product: any): string | null {
    const path = this.getCanonicalPath(product);
    if (!path) {
      return null;
    }
    return this.toAbsolute(path);
  }

  /**
   * Locale-less canonical path (/itm/{slug}?itm={id}) for a product — the form
   * shared by every locale variant of the page. Feed it to toAbsolute() for the
   * canonical of the current build, or to SeoService.setHreflangAlternates()
   * which derives each locale's URL from it.
   */
  public getCanonicalPath(product: any): string | null {
    if (!product) {
      return null;
    }
    const slug = product['product_url'] || product['url'];
    const id = product['id'];
    if (!slug || id == null) {
      return null;
    }
    return `/itm${this.ensureLeadingSlash(slug)}?itm=${id}`;
  }

  /**
   * Canonical URL derived purely from the current path and the ?itm id — needs
   * no product data, so it can be set during SSR before the API responds. The
   * product-data-based canonical set later just confirms the same value.
   */
  public getCanonicalUrlFromPath(path: string, productId: string | number): string | null {
    const canonicalPath = this.getCanonicalPathFromPath(path, productId);
    if (!canonicalPath) {
      return null;
    }
    return this.toAbsolute(canonicalPath);
  }

  /**
   * Locale-less variant of getCanonicalUrlFromPath — see getCanonicalPath.
   */
  public getCanonicalPathFromPath(path: string, productId: string | number): string | null {
    if (!path || productId == null) {
      return null;
    }
    const cleanPath = path.split('?')[0].split('#')[0];
    const idx = cleanPath.lastIndexOf('/itm/');
    if (idx === -1) {
      return null;
    }
    const slug = cleanPath.substring(idx + '/itm/'.length);
    if (!slug) {
      return null;
    }
    return `/itm/${slug}?itm=${productId}`;
  }

  /**
   * Path for routerLink — keeps category prefix when we can resolve one, so
   * browsing URLs stay semantic. Returns just the path; ?itm={id} is supplied
   * separately via the template's [queryParams] binding.
   *
   * Resolution order for the category prefix:
   *   1. product.categoriesAsArray[0].url (ProductBasicResponse, slim list)
   *   2. product.category.url (ProductDto, single-category responses)
   *   3. First parent in product.parentCategories
   *   4. fallbackCategoryUrl (caller-supplied — usually the page the user is on)
   *
   * If none resolve, returns /itm/{slug} (the canonical form) — still a valid
   * working URL thanks to the standalone /itm/:model route.
   */
  public getRouterLinkPath(product: any, fallbackCategoryUrl?: string): string | null {
    if (!product) {
      return null;
    }
    const slug = product['product_url'] || product['url'];
    if (!slug) {
      return null;
    }
    const normalizedSlug = this.ensureLeadingSlash(slug);
    const categoryUrl = this.resolvePrimaryCategoryUrl(product, fallbackCategoryUrl);
    if (categoryUrl) {
      return `${this.ensureLeadingSlash(categoryUrl)}/itm${normalizedSlug}`;
    }
    return `/itm${normalizedSlug}`;
  }

  /**
   * Full canonical URL for a category page. Strips query params (page, sort,
   * filters) so paginated/filtered variants all collapse to one canonical.
   */
  public getCategoryCanonicalUrl(categoryUrl: string): string | null {
    if (!categoryUrl) {
      return null;
    }
    return this.toAbsolute(this.ensureLeadingSlash(categoryUrl));
  }

  /**
   * Absolute canonical URL of the homepage for the current build: the sr-Latn
   * build canonicals to `origin/` and the en build to `origin/en` (no trailing
   * slash — matching the URL the /en site is actually served under).
   */
  public getHomeCanonicalUrl(): string {
    if (this.localePrefix) {
      return this.toAbsolute('');
    }
    return this.toAbsolute('/');
  }

  /**
   * Absolute URL (origin + locale prefix + path) for the current build. Use for
   * any canonical that isn't product/category-specific (blog, static pages).
   */
  public toAbsolute(localelessPath: string): string {
    const origin = this.document.location ? this.document.location.origin : '';
    return origin + this.localePrefix + localelessPath;
  }

  private resolvePrimaryCategoryUrl(product: any, fallback?: string): string | null {
    const categories = product['categoriesAsArray'];
    if (Array.isArray(categories) && categories.length > 0 && categories[0]?.url) {
      return categories[0].url;
    }
    const singleCategory = product['category'];
    if (singleCategory && singleCategory.url) {
      return singleCategory.url;
    }
    const parents = product['parentCategories'];
    if (parents && typeof parents[Symbol.iterator] === 'function') {
      for (const parent of parents) {
        const url = parent?.category?.url || parent?.url;
        if (url) {
          return url;
        }
      }
    }
    return fallback || null;
  }

  private ensureLeadingSlash(value: string): string {
    if (!value) {
      return value;
    }
    return value.startsWith('/') ? value : '/' + value;
  }
}
